import { useEffect, useState } from 'react';
import { useTranslation } from '@/lib/i18n';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Skeleton,
  Switch,
  toast,
} from '@/components/ui';
import { NotificationTemplateIdSelect } from './NotificationTemplateIdSelect';
import {
  useNotificationRuleQuery,
  useUpdateNotificationRuleMutation,
} from '../hooks/useAutoNotificationQuery';
import type { NotificationChannel } from '../types/notificationMessage';
import { TEMPLATE_LANGUAGES, type TemplateLanguageCode } from '../types/notificationTemplate';
import type {
  NotificationRule,
  NotificationRuleUpdatePayload,
  TriggerCatalogItem,
} from '../types/notificationRule';
import { mallLabel, languageLabel } from '../lib/notificationLabels';
import {
  ruleToMatrix,
  ensureChannelCells,
  setCell,
  matrixToBindings,
  type BindingMatrix,
} from '../lib/bindingMatrix';

export interface NotificationSettingsModalProps {
  triggerCode: string | null;
  catalog: TriggerCatalogItem | null;
  onClose: () => void;
}

interface DraftState {
  enabled: boolean;
  alimtalk: boolean;
  sms: boolean;
  email: boolean;
  /** 채널×언어 바인딩 매트릭스(템플릿 지정 + 언어별 on/off). */
  matrix: BindingMatrix;
  smsFallbackEnabled: boolean;
  delayMinutes: number;
}

const ruleToDraft = (rule: NotificationRule): DraftState => ({
  enabled: rule.enabled,
  alimtalk: rule.channels.includes('KAKAO_BIZ'),
  sms: rule.channels.includes('SMS'),
  email: rule.channels.includes('EMAIL'),
  matrix: ruleToMatrix(rule),
  smsFallbackEnabled: rule.smsFallbackEnabled,
  delayMinutes: rule.delayMinutes,
});

const draftToChannels = (d: DraftState): NotificationChannel[] => {
  const c: NotificationChannel[] = [];
  if (d.alimtalk) c.push('KAKAO_BIZ');
  if (d.sms) c.push('SMS');
  if (d.email) c.push('EMAIL');
  return c;
};

export const NotificationSettingsModal = ({ triggerCode, catalog, onClose }: NotificationSettingsModalProps) => {
  const { t } = useTranslation('notifications');
  const open = triggerCode !== null;
  const ruleQuery = useNotificationRuleQuery(triggerCode);
  const update = useUpdateNotificationRuleMutation();
  const [draft, setDraft] = useState<DraftState | null>(null);

  // 규칙 로드 시 드래프트 초기화(모달 열릴 때마다 최신 GET 반영).
  useEffect(() => {
    if (open && ruleQuery.data) setDraft(ruleToDraft(ruleQuery.data));
    if (!open) setDraft(null);
  }, [open, ruleQuery.data]);

  const triggerLabel = catalog?.label ?? ruleQuery.data?.label ?? triggerCode ?? '';
  const variables = catalog?.variables ?? [];

  const set = <K extends keyof DraftState>(key: K, value: DraftState[K]) =>
    setDraft((prev) => (prev ? { ...prev, [key]: value } : prev));

  // 채널 on/off — 켤 때 그 채널의 4언어 셀을 보장(기존 값 보존). 끄면 매트릭스 값은 그대로 둠
  // (재활성 시 복원). 저장 payload 는 활성 채널만 평탄화하므로 회귀 없음.
  const toggleChannel = (key: 'alimtalk' | 'sms' | 'email', channel: NotificationChannel, on: boolean) =>
    setDraft((prev) =>
      prev ? { ...prev, [key]: on, matrix: on ? ensureChannelCells(prev.matrix, channel) : prev.matrix } : prev,
    );

  // 셀(채널×언어) 패치.
  const patchCell = (
    channel: NotificationChannel,
    lang: TemplateLanguageCode,
    patch: { templateId?: string | null; enabled?: boolean },
  ) => setDraft((prev) => (prev ? { ...prev, matrix: setCell(prev.matrix, channel, lang, patch) } : prev));

  const onSave = () => {
    if (!triggerCode || !draft) return;
    const channels = draftToChannels(draft);
    const body: NotificationRuleUpdatePayload = {
      enabled: draft.enabled,
      channels,
      templateBindings: matrixToBindings(draft.matrix, channels),
      smsFallbackEnabled: draft.smsFallbackEnabled,
      delayMinutes: draft.delayMinutes,
    };
    update.mutate(
      { triggerCode, body },
      {
        onSuccess: () => {
          toast.success(t('auto.toast.saveSuccess', '자동 알림 설정이 저장되었습니다.'));
          onClose();
        },
        onError: (e) => toast.error(e.message ?? t('auto.toast.saveFail', '자동 알림 설정에 실패했습니다.')),
      },
    );
  };

  const sectionTitle = (label: string) => (
    <p
      className="border-t pt-3 text-[13px] font-semibold"
      style={{ color: 'var(--content-muted)', borderColor: 'var(--divider-soft)' }}
    >
      {label}
    </p>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('auto.settings.title', '자동 알림 설정')}</DialogTitle>
          <DialogDescription>
            {triggerLabel} · {t('auto.settings.description', '이 이벤트 발생 시 발송할 채널·템플릿·발송 시점을 설정합니다.')}
          </DialogDescription>
        </DialogHeader>

        {ruleQuery.isPending ? (
          <div className="space-y-2" role="status" aria-live="polite">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-[40px] w-full" />
            ))}
          </div>
        ) : ruleQuery.isError ? (
          <p className="py-6 text-center text-[13px]" style={{ color: 'var(--status-danger-fg)' }}>
            {t('auto.error', '자동 알림 목록을 불러오지 못했습니다.')}
          </p>
        ) : draft ? (
          <div className="mt-2 flex max-h-[60vh] flex-col gap-4 overflow-y-auto pr-1">
            {/* 활성화 */}
            <div className="flex items-center justify-between rounded-md p-3" style={{ background: 'var(--surface-1)' }}>
              <span className="text-[13px]" style={{ color: 'var(--content-default)' }}>
                {t('auto.settings.enabled', '자동 알림 사용')}
              </span>
              <Switch checked={draft.enabled} onCheckedChange={(v) => set('enabled', v)} aria-label={t('auto.settings.enabled', '자동 알림 사용')} />
            </div>

            {/* 채널 설정 */}
            {sectionTitle(t('auto.settings.channels', '발송 채널'))}
            <div className="flex flex-col gap-2">
              {([
                ['alimtalk', 'KAKAO_BIZ', t('auto.settings.alimtalk', '알림톡')],
                ['sms', 'SMS', t('auto.settings.sms', 'SMS')],
                ['email', 'EMAIL', t('auto.settings.email', '이메일')],
              ] as const).map(([key, channel, label]) => (
                <div key={key} className="flex items-center justify-between">
                  <span className="text-[13px]" style={{ color: 'var(--content-default)' }}>{label}</span>
                  <Switch
                    checked={draft[key]}
                    onCheckedChange={(v) => toggleChannel(key, channel, v)}
                    aria-label={label}
                  />
                </div>
              ))}
            </div>

            {/* 채널×언어(몰) 템플릿 지정 매트릭스 — 활성 채널마다 4언어 행. dispatch 가 그대로 발송. */}
            {draft.email || draft.alimtalk || draft.sms ? (
              <>
                {sectionTitle(t('auto.settings.templatesSection', '템플릿 연결(몰/언어별)'))}
                <p className="text-[11px]" style={{ color: 'var(--content-muted)' }}>
                  {t(
                    'auto.settings.matrixNote',
                    '수신자 국적에 해당하는 언어(몰)의 지정 템플릿으로 발송됩니다. 언어별 발송 on/off 와 템플릿을 지정하세요. 미지정 시 한국어 템플릿으로 자동 대체합니다.',
                  )}
                </p>
                <div className="flex flex-col gap-4">
                  {([
                    ['email', 'EMAIL', t('auto.settings.email', '이메일')],
                    ['alimtalk', 'KAKAO_BIZ', t('auto.settings.alimtalk', '알림톡')],
                    ['sms', 'SMS', t('auto.settings.sms', 'SMS')],
                  ] as const)
                    .filter(([key]) => draft[key])
                    .map(([, channel, channelLbl]) => (
                      <div key={channel} className="flex flex-col gap-2">
                        <span className="text-[12px] font-semibold" style={{ color: 'var(--content-strong)' }}>
                          {channelLbl}
                        </span>
                        {TEMPLATE_LANGUAGES.map((lang) => {
                          const cell = draft.matrix[channel]?.[lang] ?? { templateId: null, enabled: true };
                          return (
                            <div
                              key={lang}
                              className="flex flex-col gap-1.5 rounded-md p-2.5"
                              style={{ background: 'var(--surface-1)', border: '1px solid var(--divider-soft)' }}
                            >
                              <div className="flex items-center justify-between gap-2">
                                <div className="flex items-center gap-1.5">
                                  <span className="text-[12px] font-medium" style={{ color: 'var(--content-strong)' }}>
                                    {mallLabel(t, lang)}
                                  </span>
                                  <span className="text-[11px]" style={{ color: 'var(--content-muted)' }}>
                                    {languageLabel(t, lang)}
                                  </span>
                                </div>
                                <Switch
                                  checked={cell.enabled}
                                  onCheckedChange={(v) => patchCell(channel, lang, { enabled: v })}
                                  aria-label={`${channelLbl} ${mallLabel(t, lang)} ${t('auto.settings.langEnabled', '발송')}`}
                                />
                              </div>
                              {cell.enabled ? (
                                <NotificationTemplateIdSelect
                                  channel={channel}
                                  languageCode={lang}
                                  value={cell.templateId}
                                  onChange={(v) => patchCell(channel, lang, { templateId: v })}
                                  placeholder={t('auto.settings.templateAutoPlaceholder', '자동(한국어 템플릿 대체)')}
                                />
                              ) : (
                                <span className="text-[11px]" style={{ color: 'var(--content-muted)' }}>
                                  {t('auto.settings.langDisabled', '이 언어(몰)로는 발송하지 않습니다.')}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ))}
                </div>
              </>
            ) : null}

            {/* SMS 폴백 */}
            {draft.alimtalk ? (
              <>
                {sectionTitle(t('auto.settings.smsFallback', 'SMS 대체 발송'))}
                <div className="flex items-center justify-between">
                  <span className="text-[13px]" style={{ color: 'var(--content-default)' }}>
                    {t('auto.settings.smsFallbackMode.fallback', '알림톡 실패 시 SMS 대체')}
                  </span>
                  <Switch
                    checked={draft.smsFallbackEnabled}
                    onCheckedChange={(v) => set('smsFallbackEnabled', v)}
                    aria-label={t('auto.settings.smsFallback', 'SMS 대체 발송')}
                  />
                </div>
              </>
            ) : null}

            {/* 발송 지연 */}
            {sectionTitle(t('auto.settings.delay', '발송 지연(분)'))}
            <div className="flex flex-col gap-1">
              <Input
                type="number"
                size="sm"
                className="w-32"
                min={0}
                value={String(draft.delayMinutes)}
                onChange={(e) => set('delayMinutes', Math.max(0, Number(e.target.value) || 0))}
                aria-label={t('auto.settings.delay', '발송 지연(분)')}
              />
              <span className="text-[11px]" style={{ color: 'var(--content-muted)' }}>{t('auto.settings.delayHint', '0 이면 즉시 발송합니다.')}</span>
            </div>

            {/* 변수 안내 */}
            {sectionTitle(t('auto.settings.variables', '사용 가능한 변수'))}
            {variables.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {variables.map((v) => (
                  <span
                    key={v.name}
                    className="inline-flex h-7 items-center rounded-md px-2 font-mono text-[12px]"
                    style={{ background: 'var(--surface-1)', color: 'var(--content-default)' }}
                    title={v.label ?? v.name}
                  >
                    {`#{${v.name}}`}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-[12px]" style={{ color: 'var(--content-muted)' }}>{t('auto.settings.noVariables', '이 이벤트가 제공하는 변수가 없습니다.')}</p>
            )}
          </div>
        ) : null}

        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={onClose}>
            {t('common.cancel', '취소')}
          </Button>
          <Button size="sm" disabled={update.isPending || !draft} onClick={onSave}>
            {update.isPending ? t('common.saving', '저장 중…') : t('common.save', '저장')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
